import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpService} from '../../../common/api/http.service';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    GroupInspection,
    GroupResponseDto,
    SubgroupResponseDto,
    GroupInspectionFormData,
    GroupByOption,
    GroupInspectionFormDataResponse,
    SortOption,
    FormListDto,
    SubgroupCreateRequestDto,
    SubgroupEditRequestDto,
    SubgroupMergeRequestDto,
} from '@core/interfaces/engin/mobile-admin/group-inspections';
import {
    APIResponse,
    Filter,
    FilterFieldOption,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    PagingResponse,
} from '@core/interfaces/system/system-common';
import {TableFilterConfig} from '@core/interfaces/system/system-common';
import {map} from 'rxjs/operators';

@Injectable()
export class GroupInspectionsApi {
    private readonly prefix = 'mobile/group-inspections';
    private readonly formAssetsPrefix: string = 'mobile/form-assets';
    private readonly assetsRegistryPrefix: string = 'assets/registry';

    constructor(private api: HttpService) {}

    /**
     * Gets a ServerDataSource configured for group inspection data
     * Returns paginated, sortable, and filterable data source for the main group inspections table
     * Endpoint: GET /mobile/group-inspections/dto/list
     * @returns Observable of DataSource for the group inspection table
     */
    public getGroupInspectionData(): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.prefix}/dto/list`);
    }

    /**
     * Toggles the publish status of a group inspection
     * @param groupId The ID of the group inspection
     * @param published The new published status
     * @returns Observable of GroupResponseDto
     */
    public togglePublishStatus(groupId: number, published: boolean): Observable<GroupResponseDto> {
        return this.api.put(`${this.prefix}/${groupId}/toggle-publish?published=${published}`);
    }

    /**
     * Gets the list of subgroups for a group inspection
     * @param groupId The ID of the group inspection
     * @returns Observable of array of SubgroupResponseDto
     */
    public listSubgroups(groupId: number): Observable<SubgroupResponseDto[]> {
        return this.api.get(`${this.prefix}/${groupId}/subgroups/dto/list`);
    }

    /**
     * Creates a new group inspection
     * @param data The group inspection data to create
     * @returns Observable of GroupResponseDto
     */
    public createGroupInspection(data: GroupInspectionFormData): Observable<APIResponse<GroupInspection>> {
        return this.api.post(`${this.prefix}`, data);
    }

    /**
     * Edits an existing group inspection
     * @param groupId The ID of the group inspection
     * @param data The updated group inspection data
     * @returns Observable of GroupResponseDto
     */
    public editGroupInspection(groupId: number, data: Partial<GroupInspection>): Observable<GroupResponseDto> {
        return this.api.put(`${this.prefix}/${groupId}`, data);
    }

    /**
     * Deletes a group inspection
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    public deleteGroupInspection(groupId: number): Observable<void> {
        return this.api.delete(`${this.prefix}/${groupId}`);
    }

    /**
     * Refreshes all groups
     * @returns Observable of void
     */
    public refreshAllGroups(): Observable<void> {
        return this.api.post(`${this.prefix}/refresh`);
    }

    /**
     * Refreshes a specific group
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    public refreshGroup(groupId: number): Observable<void> {
        return this.api.put(`${this.prefix}/${groupId}/refresh`, {});
    }

    /**
     * Saves the configuration for a group inspection
     * Commits configuration and updates status to READY_FOR_PUBLISH or HAS_WARNINGS
     * @param groupId The ID of the group inspection
     * @returns Observable of GroupResponseDto
     */
    public saveConfiguration(groupId: number): Observable<GroupResponseDto> {
        return this.api.put(`${this.prefix}/${groupId}/save`);
    }

    /**
     * Update subgroup inside the given inspection group
     * @param groupId The ID of the group inspection
     * @param data The subgroup data to create
     * @returns Observable of SubgroupResponseDto
     */
    public updateSubgroup(
        groupId: number,
        data: SubgroupEditRequestDto,
    ): Observable<APIResponse<SubgroupResponseDto[]>> {
        return this.api.put(`${this.prefix}/${groupId}/subgroups`, data);
    }

    /**
     * Merges multiple subgroups into one
     * @param groupId The ID of the group inspection
     * @param data The merge data containing target and source subgroups
     * @returns Observable of void
     */
    public mergeSubgroups(
        groupId: number,
        data: SubgroupMergeRequestDto,
    ): Observable<APIResponse<SubgroupResponseDto[]>> {
        return this.api.post(`${this.prefix}/${groupId}/subgroup/merge`, data);
    }

    /**
     * Removes a group only if it's in DRAFT status
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    public deleteDraftGroup(groupId: number): Observable<void> {
        return this.api.delete(`${this.prefix}/${groupId}/remove-draft`);
    }

    /**
     * Checks if the given group label already exists
     * @param groupLabel The group label to verify
     * @returns Observable of boolean (true if available, false if exists)
     */
    public verifyGroupLabelAvailability(groupLabel: string): Observable<APIResponse<boolean>> {
        return this.api.get(`${this.prefix}/verify/groupLabel/${groupLabel}`);
    }

    /**
     * Get subgroup options for a specific group inspection
     * Returns available grouping parameter options based on the group's asset scope
     * Endpoint: GET /mobile/group-inspections/{groupId}/subgroup-options
     * @param groupId - The ID of the group inspection
     * @returns Observable of APIResponse containing array of GroupByOption
     */
    public getSubgroupOptions(groupId: number): Observable<APIResponse<GroupByOption[]>> {
        return this.api.get(`${this.prefix}/${groupId}/subgroup-options`);
    }
    /**
     * Get available icons for selection
     */
    public getAvailableIcons(): Observable<APIResponse<string[]>> {
        return this.api.get(`${this.prefix}/options/icons`);
    }
    /**
     * Get group-by options (asset class grouping options)
     * Returns mock data for now
     * @returns Observable of APIResponse containing array of GroupByOption
     */
    public getGroupByOptions(): Observable<APIResponse<GroupByOption[]>> {
        return this.api.get(`${this.prefix}/options/group-by-filters`);
    }

    /**
     * Get group inspection details by ID
     * @param groupId - Group inspection ID
     */
    public getGroupInspection(groupId: number): Observable<APIResponse<GroupInspectionFormDataResponse>> {
        return this.api.get(`${this.prefix}/${groupId}`);
    }

    /**
     * Update existing group inspection
     * @param groupId - Group inspection ID
     * @param data - Updated form data
     */
    public updateGroupInspection(
        groupId: number,
        data: GroupInspectionFormData,
    ): Observable<APIResponse<GroupInspection>> {
        return this.api.put(`${this.prefix}/${groupId}`, data);
    }

    /**
     * Get available sort options for subgroup content
     * Returns sorting options that can be applied to assets within a subgroup
     * Endpoint: GET /mobile/group-inspections/{groupInspectionId}/subgroups/{subgroupId}/sort-options
     * @param _groupInspectionId - ID of the group inspection
     * @param _subgroupId - ID of the subgroup
     * @returns Observable of APIResponse containing array of SortOption
     */
    getSortOptions(_groupInspectionId: number, _subgroupId: number): Observable<APIResponse<SortOption[]>> {
        return this.api.get(`${this.assetsRegistryPrefix}/options/columns`);
    }

    /**
     * Gets a ServerDataSource configured for inspection forms data
     * Returns all available published inspection forms that can be assigned to groups
     * Endpoint: GET /mobile/form-assets/list/dto
     * @param groupInspectionId - Optional group inspection ID to filter forms
     * @param subgroupName - Optional subgroup name to filter forms
     * @returns Observable of DataSource for the inspection forms table with pagination and filtering
     */
    public getInspectionFormsDataSource(groupInspectionId?: number, subgroupName?: string): Observable<DataSource> {
        return this.api.postServerDataSource(`${this.formAssetsPrefix}/list/dto`, {groupInspectionId, subgroupName});
    }

    /**
     * Get available filter configurations
     */
    public getGenericFilters(): Observable<APIResponse<TableFilterConfig[]>> {
        return this.api.get(`${this.assetsRegistryPrefix}/options/filters`);
        // return this.api.get(`${this.prefix}/options/filters`);
    }

    /**
     * Get filter field options dynamically
     * @param body - Request body with field key and filter params
     */
    public getFilterFieldOptions(body: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.assetsRegistryPrefix}/options/filters/field`, body);
    }

    /**
     * Gets selected inspection forms data by their IDs
     * Retrieves detailed information for a specific set of forms (used for pre-selection in form list)
     * Endpoint: GET /mobile/form-assets/list/dto?filterByfilterByformId_IN={ids}
     * @param selectedId - Array of form IDs to retrieve
     * @returns Observable of PagingResponse containing FormListDto array, or empty observable if no IDs provided
     */
    public getSelectedInspectionFormsDataSource(selectedId: number[]): Observable<PagingResponse<FormListDto>> {
        const params = 'filterByfilterByformId_IN=' + selectedId.join('|') + `&pageNumber=1&pageSize=1000`;
        return this.api.get(`${this.formAssetsPrefix}/list/dto?${params}`);
    }

    /**
     * Creates a new subgroup inside the given inspection group
     * @param groupId The ID of the group inspection
     * @param body
     * @returns Observable of SubgroupResponseDto
     */
    public createSubgroup(
        groupId: number,
        body: SubgroupCreateRequestDto,
    ): Observable<APIResponse<SubgroupResponseDto[]>> {
        return this.api.post(`${this.prefix}/${groupId}/subgroups`, body);
    }

    /**
     * Get filter options for a specific field with search capability
     * Used for dynamic loading of filter dropdown options in the form list table based on user search
     * Endpoint: POST /mobile/form-assets/options/filters/field
     * @param fieldKey - The field key to get options for (e.g., 'form_name', 'user_name')
     * @param searchString - Search string to filter options
     * @returns Observable of APIResponse containing array of FilterFieldOption
     */
    public getFilterOptions(fieldKey: string, searchString: string): Observable<APIResponse<FilterFieldOption[]>> {
        return this.api.post(`${this.formAssetsPrefix}/options/filters/field`, {
            fieldKey,
            searchString,
        });
    }

    /**
     * Get filtered asset count
     * Returns the total number of assets matching the provided filters
     * @param filterList - Array of filters to apply (optional - empty means count all)
     * @returns Observable of  number
     */
    public getAssetCount(filterList: Filter[]): Observable<number> {
        return this.api.post(`${this.assetsRegistryPrefix}/count`, filterList).pipe(map((data) => data.response));
    }
    public saveGroupConfiguration(groupId: number): Observable<APIResponse<GroupResponseDto[]>> {
        return this.api.put(`${this.prefix}/${groupId}/save`);
    }
    getGroupLabelList(): Observable<APIResponse<string[]>> {
        return this.api.get(`${this.prefix}/options/label`);
    }

    /**
     * Gets all form IDs matching current filters
     * Returns all available form IDs without pagination for "Select All" functionality
     * Uses existing /list/dto endpoint with large pageSize and extracts IDs
     * @returns Observable of APIResponse containing array of form IDs
     */
    public getAllFormIds(): Observable<APIResponse<number[]>> {
        // Reuse existing endpoint with large pageSize, then extract IDs
        const params = 'pageNumber=1&pageSize=10000';
        return this.api.get(`${this.formAssetsPrefix}/list/dto?${params}`).pipe(
            map((response: PagingResponse<FormListDto>) => ({
                response: response.items.map((form) => form.formId),
                status: 200,
                message: 'Success',
            })),
        );
    }
}
